import { Dialog, Popover } from '@mui/material'
import { useState } from 'react'
import {
  DIAGNOSIS_TABLE_COLUMN_WEIGHTS,
  PRESCRIPTION_BUILDER_VIEW_TYPES,
} from 'src/consumer/constants'
import { getFirstLetterCapitalizedString } from 'src/consumer/helpers/utilFunctions'
import {
  commonStylesPrescriptionSelect,
  loadOptionsDiagnosis,
  formatOptionLabelWithHighlight,
} from '../../PrescriptionMakingPageComponents'
import { DiagnosisCreatableSelect } from '../../PrescriptionMakingPageStyles'
import { PRESCRIPTION_COLORS_MAP } from '../common/color-utils'
import HealthEntityAddPopover from '../common/HealthEntityAddPopover'
import PrescriptionPillViewInputComponent from '../common/PrescriptionPillViewInputComponent'
import PrescriptionTableViewInputComponent from '../common/PrescriptionTableViewInputComponent'
import DiagnosisContextForm from './DiagnosisContextForm'
import DiagnosisTabularSubSectionsInput from './components/DiagnosisTabularSubSectionsInput'
import { isDiagnosisSectionIsATable } from './util'
import { handleChangeHealthEntityInputType1, handleKeyDownAndSupportEdit } from '../common/helpers'

export default function DiagnosisSelectMenu({
  clinicId,
  clinicBrandId,
  selectedDiagnosis,
  setSelectedDiagnosis,
  recommendedDiagnosis = [],
  prescriptionViewSettings,
  contextMenuSide,
  diagnosisSubSections,
  menuPortalTarget,
  maxZIndex = false,
  isLoading = false,
}) {
  const [addDiagnosisButtonAnchor, setAddDiagnosisButtonAnchor] = useState(false)
  const [diagnosisToCreate, setDiagnosisToCreate] = useState(null)

  const [inputValue, setInputValue] = useState('')

  const showTableUI = isDiagnosisSectionIsATable({ subSections: diagnosisSubSections })

  async function createDiagnosis(diagnosisToAdd) {
    setSelectedDiagnosis((prev) => [...(prev || []), diagnosisToAdd])
    //fire api to create instruction
  }

  function onCreateDiagnosis(inputValue, e) {
    const adviceToAdd = {
      apiParam: 'diagnosis',
      value: inputValue,
      label: inputValue,
      __isNew__: true,
    }
    setDiagnosisToCreate(adviceToAdd)

    if (!!e) {
      setAddDiagnosisButtonAnchor(e.currentTarget)
    } else {
      setAddDiagnosisButtonAnchor(true)
    }
  }

  function onChangeForNewUIItems(allValuesWithSelectedValues) {
    setSelectedDiagnosis(
      allValuesWithSelectedValues?.map((item) => {
        const label = item?.label || item?.value
        return { ...item, label: getFirstLetterCapitalizedString(label) }
      }),
    )
  }

  switch (prescriptionViewSettings?.prescriptionBuilderViewType) {
    case PRESCRIPTION_BUILDER_VIEW_TYPES.tabletView:
      return (
        <>
          <PrescriptionPillViewInputComponent
            additional={{
              page: 1,
              clinicId: clinicId,
              clinicBrandId: clinicBrandId,
              prescriptionViewSettings,
              recommendedDiagnosisIds: recommendedDiagnosis,
            }}
            searchPlaceholder="Diagnosis"
            onChange={onChangeForNewUIItems}
            onCreate={onCreateDiagnosis}
            prescriptionViewSettings={prescriptionViewSettings}
            debounceTimeout={200}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            getOptionsResponse={loadOptionsDiagnosis}
            value={selectedDiagnosis}
            itemName={'diagnosis'}
            pillColor={PRESCRIPTION_COLORS_MAP.diagnosis.color}
            highlightedPillColor={PRESCRIPTION_COLORS_MAP.diagnosis.highlightColor}
            title="Diagnosis"
            ContextMenuFormComponent={DiagnosisContextForm}
            contextMenuOpenSide={contextMenuSide}
            showContextFormOnClickNewCondition={(item) => item?.note || item?.location}
            useFullPageSearch={true}
          />
          <Dialog
            open={Boolean(addDiagnosisButtonAnchor)}
            onClose={() => {
              setAddDiagnosisButtonAnchor(null)
            }}
          >
            <HealthEntityAddPopover
              healthEntity={diagnosisToCreate}
              addToHealthEntityArray={createDiagnosis}
              preferredLanguages={prescriptionViewSettings?.preferredPrescriptionLanguages}
              closePopover={() => setAddDiagnosisButtonAnchor(null)}
              healthEntityName="Diagnosis"
              apiParam="diagnosis"
            />
          </Dialog>
        </>
      )
    case PRESCRIPTION_BUILDER_VIEW_TYPES.modern:
      return (
        <>
          <PrescriptionTableViewInputComponent
            value={selectedDiagnosis}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            getOptionsResponse={loadOptionsDiagnosis}
            searchPlaceholder="Diagnosis"
            onChange={onChangeForNewUIItems}
            onCreate={onCreateDiagnosis}
            debounceTimeout={200}
            prescriptionViewSettings={prescriptionViewSettings}
            additional={{
              page: 1,
              clinicId: clinicId,
              clinicBrandId: clinicBrandId,
              prescriptionViewSettings,
              recommendedDiagnosisIds: recommendedDiagnosis,
            }}
          />
          <Popover
            anchorEl={addDiagnosisButtonAnchor}
            open={Boolean(addDiagnosisButtonAnchor)}
            onClose={() => {
              setAddDiagnosisButtonAnchor(null)
            }}
          >
            <HealthEntityAddPopover
              healthEntity={diagnosisToCreate}
              addToHealthEntityArray={createDiagnosis}
              preferredLanguages={prescriptionViewSettings?.preferredPrescriptionLanguages}
              closePopover={() => setAddDiagnosisButtonAnchor(null)}
              healthEntityName="Diagnosis"
              apiParam="diagnosis"
            />
          </Popover>
        </>
      )
    default:
      return (
        <div 
          style={{ 
            width: '100%',
            position: 'relative',
            zIndex: 1000, // Ensure container has proper stacking context
          }}
        >
          {showTableUI ? (
            <>
              {/* <DiagnosisCreatableSelect
                id="diagnosisSelection"
                classNamePrefix="diagnosis_selection"
                closeMenuOnSelect={false}
                placeholder="Search or add new diagnosis..."
                isMulti={true}
                isClearable={true}
                isSearchable={true}
                defaultOptions={false}
                loadOptions={loadOptionsDiagnosis}
                getOptionValue={(option) => option?.value}
                getOptionLabel={(option) => option?.label}
                value={selectedDiagnosis}
                onChange={onChangeForNewUIItems}
                debounceTimeout={200}
                additional={{
                  page: 1,
                  clinicId: clinicId,
                  clinicBrandId: clinicBrandId,
                  recommendedDiagnosisIds: recommendedDiagnosis,
                  prescriptionViewSettings,
                }}
                styles={commonStylesPrescriptionSelect}
              /> */}
              <DiagnosisTabularSubSectionsInput
                diagnosisList={selectedDiagnosis}
                subSections={diagnosisSubSections}
                onChangeDiagnosis={onChangeForNewUIItems}
                loadDiagnosis={loadOptionsDiagnosis}
                clinicBrandId={clinicBrandId}
                clinicId={clinicId}
                loadOptionsDiagnosis={loadOptionsDiagnosis}
                prescriptionViewSettings={prescriptionViewSettings}
                recommendedDiagnosisIds={recommendedDiagnosis}
                menuPortalTarget={menuPortalTarget}
              />
            </>
          ) : (
            <DiagnosisCreatableSelect
              id="diagnosisSelection"
              classNamePrefix="diagnosis_selection"
              closeMenuOnSelect={false}
              placeholder="Diagnosis"
              isMulti={true}
              isClearable={true}
              isSearchable={true}
              defaultOptions={false}
              loadOptions={loadOptionsDiagnosis}
              getOptionValue={(option) => option?.value}
              getOptionLabel={(option) => option?.label}
              formatOptionLabel={formatOptionLabelWithHighlight}
              value={selectedDiagnosis}
              menuPortalTarget={menuPortalTarget || (typeof document !== 'undefined' ? document.body : null)}
              menuPosition={menuPortalTarget ? 'fixed' : undefined}
              onChange={onChangeForNewUIItems}
              debounceTimeout={200}
              maxZIndex={maxZIndex}
              isLoading={isLoading}
              additional={{
                page: 1,
                clinicId: clinicId,
                clinicBrandId: clinicBrandId,
                recommendedDiagnosisIds: recommendedDiagnosis,
                prescriptionViewSettings,
              }}
              styles={{
                ...commonStylesPrescriptionSelect,
                menuPortal: (provided, state) => ({
                  ...provided,
                  zIndex: state?.selectProps?.maxZIndex ? 9999 : 10000,
                }),
                menu: (provided, state) => ({
                  ...provided,
                  ...commonStylesPrescriptionSelect.menu(provided, state),
                  zIndex: 9999, // Ensure menu has high z-index
                  position: 'absolute', // Ensure proper positioning
                }),
              }}
              inputValue={inputValue}
              onInputChange={(a, b) => {
                setInputValue((prev) => {
                  if (prev === a) {
                    return prev
                  }
                  return a || ''
                })
                handleChangeHealthEntityInputType1(a, b, setSelectedDiagnosis)
              }}
              onKeyDown={(e) =>
                handleKeyDownAndSupportEdit(
                  e,
                  inputValue,
                  selectedDiagnosis,
                  setSelectedDiagnosis,
                  setInputValue,
                )
              }
            />
          )}
        </div>
      )
  }
}
